import { useMemo } from 'react';
import type { JSX } from 'react';
import type { Report } from '@/data/schemas';
import { DataTable, type ColumnDef } from './data-table';
import { ReportStatusBadge } from '@/components/blocks/feedback';
import { cn } from '@/lib/utils';
import { normalizeReportCode } from '@/utils/reportCode';

import type { SortingState, OnChangeFn } from '@tanstack/react-table';

export type ReportTableRow = {
  id: string;
  dateLabel: string;
  /** Raw date string (ISO) for sorting - use visitDate or similar */
  rawDate?: string;
  companyName?: string;
  siteName?: string;
  reportCode: string;
  ncOpen: number;
  lastUpdatedLabel: string;
  status: Report['status'];
};

interface ReportTableProps {
  rows: ReportTableRow[];
  showCompany?: boolean;
  showSite?: boolean;
  onRowClick?: (row: ReportTableRow) => void;
  emptyMessage?: string;
  density?: 'default' | 'compact';
  className?: string;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

export function ReportTable({
  rows,
  showCompany = true,
  showSite = true,
  onRowClick,
  emptyMessage,
  density = 'compact',
  className,
  sorting,
  onSortingChange,
}: ReportTableProps): JSX.Element {
  const columns = useMemo<ColumnDef<ReportTableRow>[]>(() => {
    const baseColumns: ColumnDef<ReportTableRow>[] = [
      {
        header: 'Data / Documento',
        id: 'document',
        accessorKey: 'rawDate', // Sort by raw ISO date for correct chronological order
        enableSorting: true,
        sortDescFirst: true, // Default to newest first
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-sm text-text-primary">
              {row.original.dateLabel || '—'}
            </span>
            <span className="font-mono text-xs text-text-tertiary">
              {normalizeReportCode(row.original.reportCode)}
            </span>
          </div>
        ),
      },
      {
        header: 'Azienda',
        id: 'company',
        accessorKey: 'companyName',
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className="text-sm font-medium text-text-primary block truncate max-w-[200px]"
            title={row.original.companyName}
          >
            {row.original.companyName ?? '—'}
          </span>
        ),
      },
      {
        header: 'Stabilimento',
        id: 'site',
        accessorKey: 'siteName',
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className="text-sm text-text-secondary block truncate max-w-[200px]"
            title={row.original.siteName}
          >
            {row.original.siteName ?? '—'}
          </span>
        ),
      },
      {
        header: 'NC',
        id: 'nc',
        accessorKey: 'ncOpen',
        enableSorting: true,
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.ncOpen > 0 ? (
            <div className="inline-flex items-center justify-center min-w-[2rem] px-1.5 py-0.5 rounded text-xs font-bold bg-danger-100 text-danger-500">
              {row.original.ncOpen}
            </div>
          ) : (
            <span className="text-sm text-text-muted">—</span>
          ),
      },
      {
        header: 'Aggiornato',
        id: 'updated',
        accessorKey: 'lastUpdatedLabel',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs text-text-tertiary">{row.original.lastUpdatedLabel}</span>
        ),
      },
      {
        header: 'Stato',
        id: 'status',
        accessorKey: 'status',
        enableSorting: true,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex justify-end">
            <ReportStatusBadge status={row.original.status} />
          </div>
        ),
      },
    ];

    return baseColumns.filter((column) => {
      if (column.id === 'company') return showCompany;
      if (column.id === 'site') return showSite;
      return true;
    });
  }, [showCompany, showSite]);

  return (
    <DataTable
      columns={columns}
      data={rows}
      onRowClick={onRowClick}
      emptyMessage={emptyMessage}
      density={density}
      className={cn('border-0 rounded-none bg-transparent', className)}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
}
